import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import RSButton from "ds_legacy/components/RSButton";
import { GREY_600, TEXT_DARK_SECONDARY } from "ds_legacy/materials/colors";
import { dp, margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_16, Subheading } from "ds_legacy/materials/typography";
import { useTranslations } from "translations";
import { ConfirmationDialogProps } from "./useConfirmationDialog";

function ConfirmationDialog({
  cancelText,
  componentContent,
  confirmButtonProps,
  content,
  headerIcon,
  id,
  nodeContent,
  onCancel,
  onSubmit,
  opened,
  queryProgress,
  submitText,
  sx,
  title,
  useNewDesign,
}: ConfirmationDialogProps<boolean>) {
  const translations = useTranslations();

  return useNewDesign ? (
    <Dialog
      id={id}
      data-testid={id}
      open={opened}
      PaperProps={{
        "aria-modal": "true",
        sx: {
          maxWidth: dp(400),
          padding: dp(24),
          borderRadius: dp(8),
          alignItems: "center",
          ...sx,
        },
      }}
    >
      {headerIcon && (
        <div className="flex w-full justify-center pt-2">{headerIcon}</div>
      )}
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: FONT_SIZE_16,
          lineHeight: dp(20),
          textAlign: "center",
        }}
      >
        {title ?? ""}
      </DialogTitle>
      {content && (
        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: dp(14),
              lineHeight: dp(18),
              color: GREY_600,
              textAlign: "center",
            }}
          >
            {content}
          </DialogContentText>
        </DialogContent>
      )}
      {componentContent && <DialogContent>{componentContent}</DialogContent>}
      {nodeContent && (
        <DialogContent>
          <Subheading style={{ color: TEXT_DARK_SECONDARY }}>
            {nodeContent}
          </Subheading>
        </DialogContent>
      )}
      <DialogActions
        sx={{
          flexDirection: "column",
          justifyContent: "center",
          width: "100%",
          gap: dp(8),
        }}
      >
        <RSButton
          color="primary"
          id={`confirm-${id}`}
          loading={queryProgress || "na"}
          onClick={() => onSubmit(true)}
          variant="contained"
          style={{ margin: margin(0), width: "100%" }}
          buttonWrapperStyle={{ margin: margin(0), width: "100%" }}
          {...confirmButtonProps}
        >
          {submitText || translations.actions.confirm}
        </RSButton>
        <RSButton
          color="primary"
          id={`cancel-${id}`}
          loading="na"
          onClick={onCancel}
          style={{ margin: margin(0), width: "100%" }}
          buttonWrapperStyle={{ margin: margin(0), width: "100%" }}
          variant="outlined"
        >
          {cancelText || translations.actions.cancel}
        </RSButton>
      </DialogActions>
    </Dialog>
  ) : (
    <Dialog
      id={id}
      data-testid={id}
      open={opened}
      fullWidth
      PaperProps={{
        "aria-modal": "true",
      }}
    >
      <DialogTitle>{title ?? ""}</DialogTitle>
      {content && (
        <DialogContent>
          <DialogContentText>{content}</DialogContentText>
        </DialogContent>
      )}
      {componentContent && <DialogContent>{componentContent}</DialogContent>}
      {nodeContent && (
        <DialogContent>
          <Subheading style={{ color: TEXT_DARK_SECONDARY }}>
            {nodeContent}
          </Subheading>
        </DialogContent>
      )}
      <DialogActions>
        <RSButton
          color="primary"
          id="cancel"
          loading="na"
          onClick={onCancel}
          variant="text"
        >
          {cancelText || translations.actions.cancel}
        </RSButton>
        <RSButton
          color="primary"
          id="confirm"
          loading={queryProgress || "na"}
          onClick={() => onSubmit(true)}
          variant="text"
          {...confirmButtonProps}
        >
          {submitText || translations.actions.confirm}
        </RSButton>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialog;
